"use client"

import type React from "react"

import { useState, useRef, useEffect } from "react"
import { useParams } from 'react-router-dom';
import { Button } from "../ui/button"
import { Card, CardContent } from "../ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs"
import { Progress } from "../ui/progress"
import { Badge } from "../ui/badge"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion"
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  SkipBack,
  SkipForward,
  Download,
  CheckCircle,
  Circle,
  ChevronRight,
  X,
  ArrowLeft,
  BookOpen,
  FileText,
  Clock,
  Info,
  Settings,
} from "lucide-react"
import { formationAPI } from "../../services/api"
import { useAuth } from "../../context/AuthContext"

// Type definitions based on API responses
interface Formation {
  id: number
  name: string
  image: string | null
  shortDescription: string
  longDescription: string
  categoryName: string | null
  progress: number
}

interface Section {
  id: number
  title: string
  number: number
  durationMinutes: number
  chapitreCount: number
}

interface Chapitre {
  id: number
  title: string
  description: string
  videoPath: string | null
  durationMinutes: number
  number: number
  sectionId: number
  progress: number // 0 or 100 from API
}

interface ApprendreItem {
  learningText: string
}

interface Ressource {
  id: number
  name: string
  description: string
  fileType: string
  filePath: string
}

interface ChapitreRessource {
  chapitreId: number
  chapitreTitle: string
  ressources: Ressource[]
}

interface WatchData {
  formation: Formation
  chapitreCount: number
  sections: Section[]
  chapitres: Chapitre[]
}

interface ContentData {
  apprendre: ApprendreItem[]
  chapitreRessources: ChapitreRessource[]
  formationRessources: Ressource[]
}

export default function DashboardWatch() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();

  // State for API data
  const [watchData, setWatchData] = useState<WatchData | null>(null);
  const [contentData, setContentData] = useState<ContentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI State
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [progress, setProgress] = useState(0)
  const [currentChapter, setCurrentChapter] = useState(0)
  const [currentSection, setCurrentSection] = useState(0)
  const [showMobileNav, setShowMobileNav] = useState(false)
  const [showControls, setShowControls] = useState(true)
  const [volume, setVolume] = useState(1)
  const [showYoutubePlayer, setShowYoutubePlayer] = useState(false)
  
  const [showQualityMenu, setShowQualityMenu] = useState(false)
  const [selectedQuality, setSelectedQuality] = useState('Auto')

  // Refs
  const videoRef = useRef<HTMLVideoElement>(null)
  const videoContainerRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

  // Get current user ID
  const getCurrentUserId = () => user?.id || null;

  // Fetch data on component mount
  useEffect(() => {
    if (id && user?.id) {
      fetchWatchData();
      fetchContentData();
    }
  }, [id, user?.id]);

  const fetchWatchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const userId = getCurrentUserId();
      if (!userId || !id) {
        setError('Utilisateur non connecté ou formation non trouvée');
        return;
      }

      const response = await formationAPI.getWatchFormation(parseInt(id), userId);
      
      setWatchData(response.data);
    } catch (err) {
      console.error('Error fetching watch data:', err);
      setError('Erreur lors du chargement de la formation');
    } finally {
      setLoading(false);
    }
  };

  const fetchContentData = async () => {
    try {
      if (!id) return;
      
      const response = await formationAPI.getWatchContent(parseInt(id));
      
      setContentData(response.data);
    } catch (err) {
      console.error('Error fetching content data:', err);
    }
  };

  const getYoutubeId = (url: string | null | undefined): string | null => {
    if (!url) return null;
    
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Handle chapter completion
  const handleChapterComplete = async (userId: number, chapitreId: number, formationId: number) => {
    try {
      
      const response = await formationAPI.completeChapitre({
        userId,
        chapitreId,
        formationId
      });

      if (response.data.success) {
        // Refresh the watch data to get updated progress
        await fetchWatchData();
      }
    } catch (err) {
      console.error('Error completing chapter:', err);
    }
  };

  const SimpleQualityMenu = () => {
    const qualities = ['Auto', '1080p', '720p', '480p', '360p']
    
    return (
      <div className="absolute top-full right-0 mt-2 bg-black/90 rounded-lg p-2 min-w-[120px] z-50">
        <div className="text-white text-sm font-medium mb-2 px-2">Qualité</div>
        {qualities.map((quality) => (
          <button
            key={quality}
            className={`w-full text-left px-2 py-1 text-sm rounded transition-colors ${
              quality === selectedQuality
                ? 'bg-white/20 text-white font-medium'
                : 'text-gray-300 hover:bg-white/10 hover:text-white'
            }`}
            onClick={() => handleQualityChange(quality)}
          >
            {quality}
          </button>
        ))}
      </div>
    )
  }

  // Show/hide video controls
  useEffect(() => {
    const handleMouseMove = () => {
      setShowControls(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        if (isPlaying) {
          setShowControls(false);
        }
      }, 3000);
    };

    const videoContainer = videoContainerRef.current;
    if (videoContainer) {
      videoContainer.addEventListener("mousemove", handleMouseMove);
      videoContainer.addEventListener("mouseenter", handleMouseMove);
      videoContainer.addEventListener("mouseleave", () => {
        if (isPlaying) {
          setShowControls(false);
        }
      });
    }

    return () => {
      if (videoContainer) {
        videoContainer.removeEventListener("mousemove", handleMouseMove);
        videoContainer.removeEventListener("mouseenter", handleMouseMove);
        videoContainer.removeEventListener("mouseleave", () => {
          if (isPlaying) {
            setShowControls(false);
          }
        });
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isPlaying]);

  useEffect(() => {
    const handleClickOutside = () => {
      setShowQualityMenu(false)
    }
    
    if (showQualityMenu) {
      document.addEventListener('click', handleClickOutside)
    }
    
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [showQualityMenu])

  // Get sections with chapters organized
  const getSectionsWithChapters = () => {
    if (!watchData) return [];
    
    return watchData.sections.map(section => {
      const sectionChapters = watchData.chapitres.filter(
        chapitre => chapitre.sectionId === section.id
      ).sort((a, b) => a.number - b.number);
      
      return {
        ...section,
        chapters: sectionChapters
      };
    }).sort((a, b) => a.number - b.number);
  };

  const sectionsWithChapters = getSectionsWithChapters();

  // Find current chapter
  const findCurrentChapter = () => {
    if (!watchData || sectionsWithChapters.length === 0) return null;
    
    const currentSectionData = sectionsWithChapters[currentSection];
    if (!currentSectionData || !currentSectionData.chapters[currentChapter]) {
      return null;
    }
    
    return currentSectionData.chapters[currentChapter];
  };

  const activeChapter = findCurrentChapter();

  // Calculate overall progress
  const calculateOverallProgress = () => {
    if (!watchData) return 0;
    return watchData.formation.progress;
  };

  // Navigation functions
  const changeChapter = (sectionIndex: number, chapterIndex: number) => {
    setCurrentSection(sectionIndex);
    setCurrentChapter(chapterIndex);
    setIsPlaying(false);
    setShowYoutubePlayer(false);
    
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const nextChapter = () => {
    if (!sectionsWithChapters[currentSection]) return;
    
    const currentSectionChapters = sectionsWithChapters[currentSection].chapters;

    if (currentChapter < currentSectionChapters.length - 1) {
      changeChapter(currentSection, currentChapter + 1);
    } else if (currentSection < sectionsWithChapters.length - 1) {
      changeChapter(currentSection + 1, 0);
    }
    
    setShowYoutubePlayer(false);
  };

  const prevChapter = () => {
    if (currentChapter > 0) {
      changeChapter(currentSection, currentChapter - 1);
    } else if (currentSection > 0) {
      const prevSectionChapters = sectionsWithChapters[currentSection - 1].chapters;
      changeChapter(currentSection - 1, prevSectionChapters.length - 1);
    }
    
    setShowYoutubePlayer(false);
  };

  // Video control functions
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        const playPromise = videoRef.current.play();
        
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true);
            })
            .catch(error => {
              console.error("Play error:", error);
            });
        }
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = Number.parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      setIsMuted(newVolume === 0);
    }
  };

  const toggleFullscreen = () => {
    if (videoContainerRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        videoContainerRef.current.requestFullscreen();
      }
    }
  };

  const updateProgress = () => {
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      const duration = videoRef.current.duration;
      setCurrentTime(currentTime);
      setDuration(duration);
      setProgress((currentTime / duration) * 100);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  // Calculate total duration
  const calculateTotalDuration = () => {
    if (!watchData) return "0h 0min";
    
    let totalMinutes = 0;
    watchData.chapitres.forEach((chapitre) => {
      totalMinutes += chapitre.durationMinutes;
    });

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${hours}h ${minutes}min`;
  };

  const handleQualityChange = (quality: string) => {
    setSelectedQuality(quality)
    setShowQualityMenu(false)
    
    if (videoRef.current) {
      const video = videoRef.current
      
      // Save current playback state
      const currentTime = video.currentTime
      const wasPlaying = !video.paused
      
      // Apply more noticeable quality effects
      switch(quality) {
        case '1080p':
          video.style.imageRendering = 'high-quality'
          video.style.filter = 'contrast(1.05) saturate(1.1) sharpness(1)'
          video.style.transform = 'scale(1)'
          // Simulated high quality
          break
          
        case '720p':
          video.style.imageRendering = 'optimizeQuality'
          video.style.filter = 'contrast(1.02) saturate(1.05)'
          video.style.transform = 'scale(1)'
          break
          
        case '480p':
          video.style.imageRendering = 'optimizeSpeed'
          video.style.filter = 'contrast(0.95) brightness(0.98) saturate(0.95)'
          video.style.transform = 'scale(0.98)'
          break
          
        case '360p':
          video.style.imageRendering = 'pixelated'
          video.style.filter = 'contrast(0.9) brightness(0.95) saturate(0.9) blur(0.8px)'
          video.style.transform = 'scale(0.95)'
          break
          
        default: // Auto
          video.style.imageRendering = 'auto'
          video.style.filter = 'none'
          video.style.transform = 'scale(1)'
      }
      
      // Maintain consistent container size
      video.style.maxHeight = 'none'
      video.style.height = 'auto'
      video.style.width = '100%'
      
      // Don't reload video to avoid interruption
      // video.load() - Removed this line
      
      // Restore playback state without reload
      if (currentTime > 0) {
        video.currentTime = currentTime
      }
      if (wasPlaying) {
        video.play()
      }
      
      // Visual feedback in console
      console.log(`Quality changed to: ${quality}`)
    }
  }

  // Get resources for current chapter
  const getCurrentChapterResources = () => {
    if (!contentData || !activeChapter) return [];
    
    const chapitreRes = contentData.chapitreRessources.find(
      cr => cr.chapitreId === activeChapter.id
    );
    
    return chapitreRes ? chapitreRes.ressources : [];
  };

  // Loading state
  if (loading) {
    return (
      <div className="container px-4 py-8 md:px-6 md:py-12">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400">Chargement de la formation...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !watchData) {
    return (
      <div className="container px-4 py-12 md:px-6 md:py-24 flex flex-col items-center">
        <h1 className="text-2xl font-serif font-bold mb-4">Formation non trouvée</h1>
        <p className="text-gray-500 dark:text-gray-400 mb-8">
          {error || "La formation que vous recherchez n'existe pas."}
        </p>
        <a href="/dashboard/courses">
          <Button>Retour à mes formations</Button>
        </a>
      </div>
    );
  }

  const course = watchData.formation;
  const totalChapters = watchData.chapitreCount;

  const activeChapterHasVideo = Boolean(
    activeChapter?.videoPath && String(activeChapter.videoPath).trim()
  );
  const activeChapterYoutubeId =
    activeChapterHasVideo && activeChapter?.videoPath
      ? getYoutubeId(activeChapter.videoPath)
      : null;

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header de navigation */}
      <header className="bg-white dark:bg-gray-950 border-b py-4 px-4 md:px-6 shadow-sm">
        <div className="container mx-auto flex items-center justify-between">
          <a href="/dashboard/courses" className="flex items-center text-elille-blue hover:underline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Retour à mes formations</span>
            <span className="sm:hidden">Retour</span>
          </a>
          <div className="text-sm font-medium">
            <span className="hidden md:inline">{course.name}</span>
            <span className="md:hidden">
              {course.name.length > 25 ? course.name.substring(0, 25) + "..." : course.name}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="hidden sm:flex items-center gap-1">
              <Clock className="h-3 w-3" />
              <span>{calculateTotalDuration()}</span>
            </Badge>
            <Badge variant="outline" className="hidden sm:flex items-center gap-1">
              <BookOpen className="h-3 w-3" />
              <span>{totalChapters} chapitres</span>
            </Badge>
          </div>
        </div>
      </header>

      <div className="flex flex-col lg:flex-row h-full">
        {/* Lecteur vidéo principal */}
        <div className="lg:w-3/4 bg-black relative">
          <div ref={videoContainerRef} className="relative w-full group">
            {showYoutubePlayer && activeChapterHasVideo && activeChapterYoutubeId ? (
  // YouTube iframe player
  <div className="relative w-full aspect-video">
    <iframe
      className="w-full h-full"
      src={`https://www.youtube.com/embed/${activeChapterYoutubeId}?autoplay=1`}
      title={activeChapter?.title ?? course.name}
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    ></iframe>
    
    {/* Quality Settings Button for YouTube */}
    <div className="absolute top-4 right-4">
      <div className="relative">
        <Button
          variant="ghost"
          size="sm"
          className="text-white hover:bg-white/20 bg-black/40 backdrop-blur-sm transition-all duration-200 hover:scale-105"
          onClick={(e) => {
            e.stopPropagation();
            setShowQualityMenu(!showQualityMenu);
          }}
        >
          <Settings className="h-4 w-4" />
        </Button>
        {showQualityMenu && <SimpleQualityMenu />}
      </div>
    </div>
  </div>
            ) : activeChapterHasVideo && !activeChapterYoutubeId ? (
              // Direct video file with quality controls
              <div className="relative">
                <video
                  ref={videoRef}
                  className="w-full aspect-video object-cover"
                  src={`${activeChapter?.videoPath ?? ""}`}
                  controls
                  onTimeUpdate={updateProgress}
                  onEnded={() => {
                    const userId = getCurrentUserId();
                    if (userId && activeChapter) {
                      handleChapterComplete(userId, activeChapter.id, course.id);
                    }
                  }}
                />
                
                {/* Quality Settings Button for direct video */}
                <div className="absolute top-4 right-4">
                  <div className="relative">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-white hover:bg-white/20 bg-black/40 backdrop-blur-sm transition-all duration-200 hover:scale-105"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowQualityMenu(!showQualityMenu);
                      }}
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                    {showQualityMenu && <SimpleQualityMenu />}
                  </div>
                </div>
              </div>
            ) : activeChapter && !activeChapterHasVideo ? (
              <div className="relative w-full aspect-video bg-gradient-to-br from-slate-800 to-slate-950 flex flex-col items-center justify-center px-6 py-10 text-center">
                <BookOpen className="h-14 w-14 text-slate-500 mb-4" />
                <p className="text-slate-200 text-base font-medium mb-2">{activeChapter.title}</p>
                <p className="text-slate-400 text-sm max-w-lg mb-6">
                  Il n&apos;y a pas de vidéo pour ce chapitre. Lisez la description ci-dessous, puis marquez le chapitre comme terminé pour mettre à jour votre progression.
                </p>
                {activeChapter.progress === 100 ? (
                  <Badge className="bg-green-600 hover:bg-green-600 text-white">Chapitre terminé</Badge>
                ) : (
                  <Button
                    type="button"
                    className="bg-elille-blue hover:bg-elille-blue/90"
                    onClick={async () => {
                      const userId = getCurrentUserId();
                      if (userId && activeChapter) {
                        await handleChapterComplete(userId, activeChapter.id, course.id);
                      }
                    }}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Marquer comme terminé
                  </Button>
                )}
              </div>
            ) : (
              // Video thumbnail with play button
              <div className="relative">
                <img
                  src={course.image ? `${course.image}` : "/placeholder.svg"}
                  alt={activeChapter?.title || course.name}
                  className="w-full aspect-video object-cover"
                />
                <div
                  className="absolute inset-0 flex items-center justify-center bg-black/50 cursor-pointer"
                  onClick={() => {
                    if (activeChapterHasVideo && activeChapterYoutubeId) {
                      setShowYoutubePlayer(true);
                    }
                  }}
                >
                  <div className="bg-black/40 p-5 rounded-full backdrop-blur-sm">
                    <Play className="h-16 w-16 text-white" />
                  </div>
                </div>
                
                {/* Quality Settings Button for thumbnail */}
                <div className="absolute top-4 right-4">
                  <div className="relative">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-white hover:bg-white/20 bg-black/40 backdrop-blur-sm transition-all duration-200 hover:scale-105"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowQualityMenu(!showQualityMenu);
                      }}
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                    {showQualityMenu && <SimpleQualityMenu />}
                  </div>
                </div>
              </div>
            )}

            {/* Overlay for title (only shown when not in YouTube mode) */}
            {!showYoutubePlayer && activeChapter && (
              <div
                className={`absolute top-4 left-4 right-20 bg-black/70 p-3 rounded-lg transition-opacity duration-300 ${
                  showControls ? "opacity-100" : "opacity-0"
                }`}
              >
                <h3 className="text-white font-medium text-sm md:text-base">
                  {sectionsWithChapters[currentSection]?.title} - {activeChapter.title}
                </h3>
              </div>
            )}
          </div>

          {/* Informations sur le chapitre */}
          {activeChapter && (
            <div className="p-6 bg-white dark:bg-gray-900 shadow-md">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="secondary" className="font-normal">
                      {sectionsWithChapters[currentSection]?.title}
                    </Badge>
                    <Badge variant="outline" className="font-normal">
                      {activeChapter.durationMinutes} min
                    </Badge>
                    {activeChapter.progress === 100 && (
                      <Badge variant="default" className="bg-green-500 text-white">
                        ✓ Terminé
                      </Badge>
                    )}
                  </div>
                  <h2 className="text-xl font-bold">{activeChapter.title}</h2>
                </div>
                <div className="lg:hidden">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowMobileNav(!showMobileNav)}
                    className="flex items-center gap-1"
                  >
                    {showMobileNav ? <ChevronRight className="h-4 w-4" /> : <BookOpen className="h-4 w-4" />}
                    <span className="hidden sm:inline">{showMobileNav ? "Masquer" : "Chapitres"}</span>
                  </Button>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-300">{activeChapter.description}</p>
            </div>
          )}

          {/* Onglets d'informations */}
          <div className="p-6 bg-white dark:bg-gray-900 border-t">
            <Tabs defaultValue="description" className="w-full">
              <TabsList className="grid w-full grid-cols-2 md:w-auto">
                <TabsTrigger value="description" className="flex items-center gap-1">
                  <Info className="h-4 w-4 hidden sm:inline" />
                  <span>À propos</span>
                </TabsTrigger>
                <TabsTrigger value="resources" className="flex items-center gap-1">
                  <FileText className="h-4 w-4 hidden sm:inline" />
                  <span>Ressources</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="description" className="mt-6">
                <div className="prose dark:prose-invert max-w-none">
                  <h3 className="text-lg font-medium mb-3">Description du cours</h3>
                  <p>{course.longDescription}</p>

                  <h4 className="text-md font-medium mt-6 mb-3">Ce que vous allez apprendre</h4>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {contentData?.apprendre.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>{item.learningText}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </TabsContent>

              <TabsContent value="resources" className="mt-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium mb-3">Ressources du chapitre</h3>
                  {getCurrentChapterResources().length > 0 ? (
                    getCurrentChapterResources().map((resource, index) => (
                      <Card key={index} className="overflow-hidden hover:shadow-md transition-shadow">
                        <CardContent className="p-0">
                          <div className="flex items-center justify-between p-4">
                            <div className="flex items-center">
                              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg mr-4">
                                <FileText className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                              </div>
                              <div>
                                <h4 className="font-medium">{resource.name}</h4>
                                <p className="text-sm text-gray-500 dark:text-gray-400">{resource.description}</p>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-elille-blue hover:text-elille-blue/90 hover:bg-elille-blue/10"
                              onClick={() => window.open(resource.filePath, '_blank')}
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Télécharger
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p>Aucune ressource disponible pour ce chapitre</p>
                    </div>
                  )}

                  <h3 className="text-lg font-medium mt-8 mb-3">Ressources du cours</h3>
                  {contentData?.formationRessources && contentData.formationRessources.length > 0 ? (
                    contentData.formationRessources.map((resource, index) => (
                      <Card key={index} className="overflow-hidden hover:shadow-md transition-shadow">
                        <CardContent className="p-0">
                          <div className="flex items-center justify-between p-4">
                            <div className="flex items-center">
                              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg mr-4">
                                <FileText className="w-6 h-6 text-[#00088E] dark:text-blue-400" />
                              </div>
                              <div>
                                <h4 className="font-medium">{resource.name}</h4>
                                <p className="text-sm text-gray-500 dark:text-gray-400">{resource.description}</p>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-elille-blue hover:text-elille-blue/90 hover:bg-elille-blue/10"
                              onClick={() => window.open(resource.filePath, '_blank')}
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Télécharger
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p>Aucune ressource disponible pour ce cours</p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Liste des chapitres (sidebar) */}
        <div
          className={`lg:w-1/4 bg-white dark:bg-gray-800 border-l overflow-auto ${showMobileNav ? "fixed inset-0 z-50 lg:relative" : "hidden lg:block"}`}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold">Contenu du cours</h3>
              {showMobileNav && (
                <Button variant="ghost" size="sm" className="lg:hidden" onClick={() => setShowMobileNav(false)}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium">Progression du cours</span>
                <span className="text-sm font-medium">{calculateOverallProgress()}%</span>
              </div>
              <Progress value={calculateOverallProgress()} className="h-2" />
            </div>

            <Accordion type="multiple" defaultValue={[`section-${currentSection}`]} className="space-y-2">
              {sectionsWithChapters.map((section, sectionIndex) => (
                <AccordionItem
                  key={`section-${sectionIndex}`}
                  value={`section-${sectionIndex}`}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                >
                  <AccordionTrigger className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <div className="flex flex-col items-start text-left">
                      <span className="font-medium">{section.title}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {section.chapters.length} chapitres • {section.durationMinutes} min
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-0 pb-0">
                    <div className="space-y-1 divide-y divide-gray-100 dark:divide-gray-800">
                      {section.chapters.map((chapter, chapterIndex) => (
                        <button
                          key={`chapter-${sectionIndex}-${chapterIndex}`}
                          className={`w-full flex items-start p-4 text-left transition-colors ${
                            currentSection === sectionIndex && currentChapter === chapterIndex
                              ? "bg-elille-blue/10 text-elille-blue"
                              : "hover:bg-gray-50 dark:hover:bg-gray-700"
                          }`}
                          onClick={() => {
                            changeChapter(sectionIndex, chapterIndex);
                            if (showMobileNav) {
                              setShowMobileNav(false);
                            }
                            setShowYoutubePlayer(false);
                          }}
                        >
                          <div className="flex-shrink-0 mr-3 mt-0.5">
                            {chapter.progress === 100 ? (
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            ) : currentSection === sectionIndex && currentChapter === chapterIndex ? (
                              <Play className="h-5 w-5 text-elille-blue" />
                            ) : (
                              <Circle className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-sm truncate">{chapter.title}</h4>
                            <div className="flex items-center mt-1">
                              <Clock className="h-3 w-3 text-gray-500 dark:text-gray-400 mr-1" />
                              <span className="text-xs text-gray-500 dark:text-gray-400">{chapter.durationMinutes} min</span>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {/* Navigation buttons */}
            <div className="mt-6 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={prevChapter}
                disabled={currentSection === 0 && currentChapter === 0}
                className="flex items-center gap-1"
              >
                <SkipBack className="h-4 w-4" />
                <span className="hidden sm:inline">Précédent</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={nextChapter}
                disabled={
                  currentSection === sectionsWithChapters.length - 1 && 
                  currentChapter === (sectionsWithChapters[currentSection]?.chapters.length || 1) - 1
                }
                className="flex items-center gap-1"
              >
                <span className="hidden sm:inline">Suivant</span>
                <SkipForward className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
